import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PasswordInput from "@/components/PasswordInput";
import LanguageSelect from "@/components/LanguageSelect";
import { toast } from "sonner";
import { Leaf, ChevronLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialMode = sp.get("mode") === "signup" ? "signup" : "signin";
  const [mode, setMode] = useState(initialMode);
  const { login, register } = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("citizen");
  const [dob, setDob] = useState(""); // YYYY-MM-DD from <input type="date">
  const [acceptAll, setAcceptAll] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  // Max date-of-birth = today. Min ~120y ago to keep native picker friendly.
  const _today = new Date();
  const _maxDob = _today.toISOString().slice(0, 10);
  const _minDob = new Date(_today.getFullYear() - 120, 0, 1).toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await login(email, password, remember);
        toast.success(t("welcome_back"));
      } else {
        // Client-side quick guards to give the user immediate feedback before
        // the backend rejects. Backend enforcement is still authoritative.
        if (!dob) { toast.error(t("signup_dob_invalid")); setBusy(false); return; }
        if (!acceptAll) { toast.error(t("signup_terms_required")); setBusy(false); return; }
        // Compute integer age locally: users below 14 get a friendly error.
        const [y, m, d] = dob.split("-").map((x) => parseInt(x, 10));
        const b = new Date(y, (m || 1) - 1, d || 1);
        const now = new Date();
        let age = now.getFullYear() - b.getFullYear();
        if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age -= 1;
        if (!Number.isFinite(age) || age < 14) { toast.error(t("signup_age_required")); setBusy(false); return; }
        await register({
          email, password, name, username, role,
          date_of_birth: dob,
          terms_accepted: acceptAll,
          privacy_accepted: acceptAll,
          community_rules_accepted: acceptAll,
        });
        toast.success(t("welcome_new"));
      }
      nav("/map");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="auth-page">
      <div className="hidden md:flex relative bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="grain-overlay absolute inset-0" />
        <Link to="/" className="relative flex items-center gap-2 text-white/90 hover:text-white" data-testid="auth-back-home">
          <ChevronLeft size={18} /> <span className="text-sm">{t("auth_home")}</span>
        </Link>
        <div className="relative">
          <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
            <Leaf size={22} />
          </div>
          <div className="font-heading text-4xl md:text-5xl font-bold leading-tight">{t("auth_slogan")}</div>
          <p className="mt-6 text-white/80 max-w-md leading-relaxed">{t("hero_subtitle")}</p>
        </div>
        <div className="relative inline-flex items-center gap-1"><BrandLogo className="h-8" /></div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-[#F7F5F0]">
        <div className="w-full max-w-md">
          <div className="mb-4 flex justify-end">
            <LanguageSelect testid="auth-lang-select" />
          </div>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{mode === "signin" ? t("auth_signin_kicker") : t("auth_signup_kicker")}</div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">
              {mode === "signin" ? t("auth_signin_h") : t("auth_signup_h")}
            </h1>
          </div>

          <Button data-testid="google-signin-btn" onClick={googleLogin} type="button" variant="outline" className="w-full h-12 rounded-full border-slate-300 mb-4 hover:bg-slate-900 hover:text-white">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
            {t("cta_google")}
          </Button>

          <div className="flex items-center gap-3 my-4 text-xs uppercase tracking-widest text-slate-500">
            <div className="flex-1 h-px bg-slate-200" />
            <span>{t("auth_or")}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <>
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("name")}</Label>
                  <Input id="name" data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="username" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("username_label")}</Label>
                  <Input
                    id="username"
                    data-testid="signup-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
                    required
                    minLength={3}
                    maxLength={30}
                    pattern="[A-Za-z0-9._]{3,30}"
                    autoComplete="off"
                    className="mt-1.5 h-11 rounded-xl"
                    placeholder={t("username_placeholder")}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">{t("username_help")}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("role")}</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="signup-role" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">{t("citizen")}</SelectItem>
                      <SelectItem value="volunteer">{t("volunteer")}</SelectItem>
                      <SelectItem value="association">{t("association")}</SelectItem>
                      <SelectItem value="company">{t("company")}</SelectItem>
                      <SelectItem value="municipality">{t("municipality")}</SelectItem>
                      <SelectItem value="operator">{t("operator")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="dob" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("signup_dob_label")}</Label>
                  <Input
                    id="dob"
                    data-testid="signup-dob"
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    min={_minDob}
                    max={_maxDob}
                    required
                    className="mt-1.5 h-11 rounded-xl"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">{t("signup_dob_help")}</p>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("email")}</Label>
              <Input id="email" data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("password")}</Label>
              <PasswordInput testId="auth-password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 h-11 rounded-xl" />
            </div>
            {mode === "signin" && (
              <div className="pt-1" data-testid="signin-remember-block">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="signin-remember-me"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>{t("signin_remember_me")}</span>
                </label>
              </div>
            )}
            {mode === "signup" && (
              <div className="space-y-2.5 pt-1" data-testid="signup-consent-block">
                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="signup-accept-all"
                    checked={acceptAll}
                    onChange={(e) => setAcceptAll(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>
                    {t("signup_consent_prefix")}{" "}
                    <Link to="/terms" target="_blank" data-testid="signup-terms-link" className="text-emerald-700 font-semibold underline">
                      {t("signup_terms_link")}
                    </Link>{" "}
                    {t("signup_consent_and_the")}{" "}
                    <Link to="/community-rules" target="_blank" data-testid="signup-community-link" className="text-emerald-700 font-semibold underline">
                      {t("signup_community_link")}
                    </Link>{" "}
                    {t("signup_consent_privacy_note")}{" "}
                    <Link to="/privacy" target="_blank" data-testid="signup-privacy-link" className="text-emerald-700 font-semibold underline">
                      {t("signup_privacy_link")}
                    </Link>
                    .
                  </span>
                </label>
              </div>
            )}
            <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-base font-semibold">
              {busy ? "..." : mode === "signin" ? t("signin_now") : t("signup_now")}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            {mode === "signin" ? (
              <>
                {t("new_here")} <button data-testid="switch-to-signup" onClick={() => setMode("signup")} className="text-emerald-700 font-semibold hover:underline">{t("signup_now")}</button>
                <div className="mt-2"><Link to="/password-reset" data-testid="forgot-password-link" className="text-slate-500 hover:text-emerald-700 text-sm underline">{t("forgot_password")}</Link></div>
              </>
            ) : (
              <>{t("already")} <button data-testid="switch-to-signin" onClick={() => setMode("signin")} className="text-emerald-700 font-semibold hover:underline">{t("signin_now")}</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
